import { useQuery } from "@tanstack/react-query";
import ReactPlayer from "react-player";
import {
  Loader2,
  FileText,
  HelpCircle,
  Download,
  FileIcon,
  AlertCircle,
  Clock,
} from "lucide-react";
import { SectionPlayer, type Section } from "@/components/content";
import { api } from "@/lib/api";
import type { ApiResponse } from "@learn-me/types";

interface ChapterPreviewData {
  id: string;
  title: string;
  description?: string;
  videoUrl?: string;
  videoDuration?: number;
  requiresQuizPass: boolean;
  minQuizScore: number;
  attachments: Array<{
    id: string;
    name: string;
    url: string;
    type: string;
    size?: number;
  }>;
  quiz?: {
    id: string;
    title: string;
    passingScore: number;
    timeLimit?: number;
    questions: Array<{
      id: string;
    }>;
  };
}

interface StudentPreviewModeProps {
  chapterId: string;
  courseId: string;
  sections: Section[];
  currentSectionIndex: number;
  onSectionChange: (index: number) => void;
}

/**
 * Vista previa del capítulo tal como lo verá el estudiante.
 * Muestra video, secciones, quiz y attachments en modo lectura.
 */
export function StudentPreviewMode({
  chapterId,
  sections,
  currentSectionIndex,
  onSectionChange,
}: StudentPreviewModeProps) {
  // Fetch complete chapter data (video, quiz, attachments)
  const { data: chapter, isLoading } = useQuery({
    queryKey: ["chapter-preview", chapterId],
    queryFn: async () => {
      const response = await api.get<ApiResponse<ChapterPreviewData>>(
        `/chapters/${chapterId}`,
      );
      return response.data.data;
    },
    enabled: !!chapterId,
    staleTime: 30000,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-8 w-8 animate-spin text-primary-500" />
      </div>
    );
  }

  const hasVideo = !!chapter?.videoUrl;
  const hasQuiz = !!chapter?.quiz;
  const hasAttachments = chapter?.attachments && chapter.attachments.length > 0;

  return (
    <div className="h-full bg-white">
      <div className="max-w-4xl mx-auto p-6">
        {/* Video Player */}
        {hasVideo && chapter?.videoUrl && (
          <div className="aspect-video rounded-lg overflow-hidden bg-black mb-6">
            <ReactPlayer
              url={chapter.videoUrl}
              width="100%"
              height="100%"
              controls
              config={{
                youtube: {
                  playerVars: { modestbranding: 1 },
                },
              }}
            />
          </div>
        )}

        {/* Sections Content - same structure as CoursePlayerPage */}
        {sections.length > 0 && (
          <div className="mb-8">
            <SectionPlayer
              sections={sections}
              chapterTitle={chapter?.title}
              initialSection={currentSectionIndex}
              onSectionChange={onSectionChange}
            />
          </div>
        )}

        {/* Attachments - same structure as CoursePlayerPage */}
        {hasAttachments && chapter?.attachments && (
          <div className="mb-8">
            <h3 className="font-semibold mb-4">Material Descargable</h3>
            <div className="space-y-2">
              {chapter.attachments.map((attachment) => (
                <div
                  key={attachment.id}
                  className="flex items-center gap-3 p-3 border rounded-lg bg-gray-50"
                >
                  <FileIcon className="h-5 w-5 text-muted-foreground" />
                  <span className="flex-1">{attachment.name}</span>
                  <Download className="h-4 w-4 text-muted-foreground" />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Quiz Preview Card */}
        {hasQuiz && chapter?.quiz && (
          <div className="mb-8">
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <HelpCircle className="h-5 w-5 text-purple-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-purple-900">
                    Quiz: {chapter.quiz.title}
                  </h3>
                  <div className="mt-2 space-y-1 text-sm text-purple-700">
                    <p className="flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      {chapter.quiz.questions.length} preguntas
                    </p>
                    {chapter.quiz.timeLimit && (
                      <p className="flex items-center gap-2">
                        <Clock className="h-4 w-4" />
                        {chapter.quiz.timeLimit} minutos
                      </p>
                    )}
                    {chapter.requiresQuizPass && (
                      <p className="flex items-center gap-2">
                        <AlertCircle className="h-4 w-4" />
                        Puntaje mínimo: {chapter.minQuizScore}%
                      </p>
                    )}
                  </div>
                  <div className="mt-3">
                    <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-purple-200 text-purple-800 rounded">
                      Vista previa - No ejecutable
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Empty State */}
        {!hasVideo && sections.length === 0 && !hasQuiz && !hasAttachments && (
          <div className="text-center py-12 text-gray-500">
            <p>Este capítulo no tiene contenido visible para el estudiante.</p>
            <p className="text-sm mt-2">
              Agrega video, secciones, quiz o material descargable.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
